import React from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import { useLockedBody } from 'usehooks-ts';

import { cn } from '@restworld/utils-common';

import { TextEffect } from '../../motion-primitives/text-effect';

import useRestaurantOnboardingDataQuery, { isExpectedLookupOutcome } from './use-restaurant-onboarding-data-query';
import { activeRestaurantOnboardingStateAtom, isManualEntryAtom } from './restaurant-onboarding-atoms';
import { activeRestaurantAtom, activeRestaurantSuggestionAtom } from './google-address-input-result';
import type { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import VenueLookupError from './venue-lookup-error';
import { getManualEntryPrefill } from './manual-entry-prefill';
import startIconAnimationData from './star-icon-animation-loader.json';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

// The robot animation is ~1 MB of JSON. A static import put it in the chunk of every screen
// that can reach this loader, the venue search of /getting-started included, so it is fetched
// alongside lottie-react only when the loader actually renders.
const useRobotAnimationData = () => {
  const [data, setData] = React.useState<unknown>(null);
  React.useEffect(() => {
    let cancelled = false;
    import('./robot-animation.json').then((module) => {
      if (!cancelled) setData(module.default);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return data;
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface RestaurantOnboardingAiMagicLoadingProps {
  contentLabels?: {
    loading: string;
    action: string;
    so: string;
    save_your_time: string;
  };
  /**
   * False when the loader only stands in for another wait (the JP onboarding's job ad
   * generation). The venue lookup hook still runs inside it, and a failure of a lookup left
   * from earlier in the session must not replace that wait with the venue error screen.
   */
  isVenueLookup?: boolean;
}

// Lock the loader to the viewport so the robot + headings always sit centered
// and never trigger a vertical scroll on small mobile screens (Lottie sources
// render at their natural SVG size otherwise).
const Container = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col gap-4 text-center items-center justify-center w-full relative',
      'min-h-[100dvh] md:min-h-0 md:h-full',
      className
    )}
    {...props}
  />
);

const StarLottieWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-20 h-20 md:w-24 md:h-24', className)} {...props} />
);

const RobotLottieWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-48 h-48 md:w-56 md:h-56', className)} {...props} />
);

const Heading = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h4 className={cn('self-stretch text-neutral-700 text-xl font-semibold', className)} {...props} />
);

const SubTextContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch text-center justify-start', className)} {...props} />
);

const SubText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-200 text-base font-medium', className)} {...props} />
);

const BoldSubText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-200 text-base font-bold', className)} {...props} />
);

const RestaurantOnboardingAiMagicLoading: React.FC<RestaurantOnboardingAiMagicLoadingProps> = ({
  contentLabels,
  isVenueLookup = true,
}) => {
  const { t } = useTranslation('restaurant-onboarding');
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const robotAnimationData = useRobotAnimationData();
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  const suggestion = useAtomValue(activeRestaurantSuggestionAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  // The venue form, from restaurant-onboarding.tsx. Null only outside any react-hook-form provider.
  const form = useFormContext<RestaurantOnboardingPayload>();

  const query = useRestaurantOnboardingDataQuery();

  // When data is already cached (e.g. user navigated back), the queryFn won't re-run
  // so the UI would stay stuck on the loading screen. Transition immediately on cache hit.
  React.useEffect(() => {
    if (query.isSuccess && query.data) {
      setActiveUI('restaurant_onboarding_form');
    }
  }, [query.isSuccess, query.data, setActiveUI]);

  // 409 and not_horeca move to their own screen from the query itself; any other failure ends
  // here, once the query has given up, instead of leaving the loader spinning forever.
  const hasFailed = isVenueLookup && query.isError && !query.isFetching && !isExpectedLookupOutcome(query.error);
  // The loader covers the viewport, so the page behind it must not scroll while it spins. Released
  // on failure, where the error screen takes its place, and on unmount.
  useLockedBody(!hasFailed, '__next');
  const { refetch } = query;
  const retry = React.useCallback(() => refetch(), [refetch]);
  // The lookup failed on a venue the employer picked, almost always the right one, so the way out
  // is the manual form, prefilled with the name and address of that Google suggestion when they
  // are known. Same switches as the "Aggiungilo a mano" link under the suggestions.
  const addManually = React.useCallback(() => {
    const prefill = getManualEntryPrefill(activeRestaurant, suggestion);
    if (prefill && form) {
      form.setValue('restaurant_name', prefill.name, { shouldValidate: true });
      form.setValue('address', prefill.address, { shouldValidate: true });
    }
    setIsManualEntry(true);
    setActiveUI('restaurant_onboarding_form');
  }, [activeRestaurant, suggestion, form, setIsManualEntry, setActiveUI]);

  if (hasFailed) {
    return <VenueLookupError venueName={activeRestaurant || undefined} onRetry={retry} onAddManually={addManually} />;
  }

  return (
    <Container>
      <StarLottieWrapper>
        <Lottie loop animationData={startIconAnimationData} />
      </StarLottieWrapper>

      <Heading>
        <TextEffect per="char" preset="fade">
          {contentLabels?.loading || t('wait_just_a_couple_of_seconds')}
        </TextEffect>
      </Heading>
      <SubTextContainer>
        <SubText>
          <TextEffect per="char" preset="fade">
            {contentLabels?.action || t('we_are_searching_info_on_your_venue')}
          </TextEffect>
        </SubText>
        <div className="flex self-stretch text-center justify-center gap-1">
          <SubText>
            <TextEffect per="char" preset="fade">
              {contentLabels?.so || t('so')}
            </TextEffect>
          </SubText>
          <BoldSubText>
            <TextEffect per="char" preset="fade">
              {contentLabels?.save_your_time || t('you_dont_have_to_input_them')}
            </TextEffect>
          </BoldSubText>
        </div>
      </SubTextContainer>
      <RobotLottieWrapper>
        {robotAnimationData && <Lottie loop animationData={robotAnimationData} />}
      </RobotLottieWrapper>
    </Container>
  );
};

export default RestaurantOnboardingAiMagicLoading;
